import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import './ExamScheduleList.css';
import { useNavigate } from 'react-router-dom';
import Swal from 'sweetalert2';
import {ExamRulesApprovalModal, useExamRulesApproval} from './examRulesApproval';
import {CalendarExportButton, createCalendarDateTime} from '../../utils/calendarExport';

const API_URL = process.env.REACT_APP_API_URL;
const CUSTOM_URL = `${API_URL}/Custom`;
const GET_PERIODS_URL = `${API_URL}/Custom/GetExamPeriods`;
const GET_TIME_SLOTS_URL = `${API_URL}/Custom/GetTimeSlots`;
const GET_MY_SEAT_SCHEDULE = `${API_URL}/Custom/GetMySeatSchedule`;
const GET_STUDENT_EEXAM_RESULT = `${CUSTOM_URL}/GetStudentEExamResult`;

export default function MySeatScheduleComponent() {
    const navigate = useNavigate();
    const [publishedPeriods, setPublishedPeriods] = useState([]);
    const [selectedPeriodIndex, setSelectedPeriodIndex] = useState(null);
    const [timeSlots, setTimeSlots] = useState([]);
    const [distinctTimes, setDistinctTimes] = useState([]);
    const [weeks, setWeeks] = useState([]);
    const [currentWeek, setCurrentWeek] = useState(0);
    const [seats, setSeats] = useState([]);
    const [isLoading, setIsLoading] = useState(true);

    // For mobile aggregated view
    const [mobileData, setMobileData] = useState([]);

    const selectedPeriod = selectedPeriodIndex == null ? null : publishedPeriods[selectedPeriodIndex];
    const examRulesApproval = useExamRulesApproval(selectedPeriod?.id, 'student');
    const rulesAccepted = examRulesApproval.canView;

    // 1) Load published periods
    useEffect(() => {
        axios.get(GET_PERIODS_URL)
            .then(({ data: periods }) => {
                const pubs = (periods || []).filter(ep => ep.isPublishedStudent);
                setPublishedPeriods(pubs);
                if (pubs.length) {
                    setSelectedPeriodIndex(0);
                }
            })
            .catch(err => console.error(err))
            .finally(() => setIsLoading(false));
    }, []);

    // 2) Desktop: when selectedPeriodIndex changes, load slots & seats
    useEffect(() => {
        if (!rulesAccepted) {
            setSeats([]);
            setTimeSlots([]);
            setDistinctTimes([]);
            setWeeks([]);
            return;
        }
        if (selectedPeriodIndex == null) return;
        if (!publishedPeriods.length) return;
        if (selectedPeriodIndex < 0 || selectedPeriodIndex >= publishedPeriods.length) return;

        const periodId = publishedPeriods[selectedPeriodIndex].id;
        setIsLoading(true);
        setSeats([]);
        setTimeSlots([]);

        axios.get(`${GET_TIME_SLOTS_URL}?examPeriodId=${periodId}`)
            .then(({ data: slots }) => {
                setTimeSlots(slots);

                // distinctTimes
                const times = Array.from(new Set(
                    slots.map(ts => `${ts.startTime}-${ts.endTime}`)
                )).map(str => {
                    const [startTime, endTime] = str.split('-');
                    return { startTime, endTime };
                }).sort((a, b) => a.startTime.localeCompare(b.startTime));
                setDistinctTimes(times);

                // weeks calculation
                const dates = Array.from(new Set(
                    slots.map(ts => ts.exactDate.split('T')[0])
                )).sort();
                if (!dates.length) return [];
                const minD = new Date(dates[0]), maxD = new Date(dates[dates.length - 1]);
                const firstMon = new Date(minD);
                firstMon.setDate(minD.getDate() - ((minD.getDay() + 6) % 7));
                const lastSun = new Date(maxD);
                lastSun.setDate(maxD.getDate() + (6 - ((maxD.getDay() + 6) % 7)));
                const wks = [];
                for (let w = new Date(firstMon); w <= lastSun; w.setDate(w.getDate() + 7)) {
                    const wk = [];
                    for (let i = 0; i < 7; i++) {
                        const d = new Date(w);
                        d.setDate(w.getDate() + i);
                        wk.push(d.toISOString().split('T')[0]);
                    }
                    wks.push(wk);
                }
                setWeeks(wks);

                return axios.get(`${GET_MY_SEAT_SCHEDULE}?periodId=${periodId}`);
            })
            .then(({ data }) => {
                setSeats(data || []);
            })
            .catch(err => console.error(err))
            .finally(() => setIsLoading(false));
    }, [selectedPeriodIndex, publishedPeriods, rulesAccepted]);

    // 3) Reset currentWeek whenever weeks change
    useEffect(() => {
        if (weeks.length) setCurrentWeek(0);
    }, [weeks]);

    // 4) Mobile: selected period slots & seats
    useEffect(() => {
        if (!rulesAccepted) {
            setMobileData([]);
            return;
        }
        if (!selectedPeriod) return;

        axios
            .get(`${GET_TIME_SLOTS_URL}?examPeriodId=${selectedPeriod.id}`)
            .then(({ data: slots }) =>
                axios
                    .get(`${GET_MY_SEAT_SCHEDULE}?periodId=${selectedPeriod.id}`)
                    .then(({ data: seats }) => ({ periodName: selectedPeriod.name, slots, seats }))
            )
            .then(({periodName, slots, seats}) => {
                const combined = [];
                seats.forEach(seat => {
                    const slot = slots.find(ts => ts.id === seat.slotId);
                    if (!slot) return;
                    combined.push({
                        periodName,
                        date: slot.exactDate.split('T')[0],
                        time: { startTime: slot.startTime, endTime: slot.endTime },
                        seat
                    });
                });
                setMobileData(combined);
            })
            .catch(err => console.error(err));
    }, [selectedPeriod, rulesAccepted]);

    // VisibleDays for desktop
    const visibleDays = useMemo(() => {
        if (!weeks.length) return [];
        return weeks[currentWeek].filter(day => {
            const dow = new Date(day).getDay();
            if (dow === 0 || dow === 6) {
                return timeSlots.some(ts => ts.exactDate.startsWith(day));
            }
            return true;
        });
    }, [weeks, currentWeek, timeSlots]);

    // Mobile: unique dates across all periods
    const mobileDates = useMemo(() => {
        return Array.from(new Set(mobileData.map(e => e.date))).sort();
    }, [mobileData]);

    const showEExamResult = (seat) => {
        axios
            .get(GET_STUDENT_EEXAM_RESULT, { params: { examSeatAssignmentId: seat.examSeatAssignmentId } })
            .then(({ data }) => {
                const maxScore = data?.maxScore ?? '-';
                const score = data?.score ?? '-';
                Swal.fire({
                    title: 'Sınav Sonucum',
                    html: `<div class="fs-4 fw-bold">${score} / ${maxScore}</div>`,
                    icon: 'info',
                    confirmButtonText: 'Tamam'
                });
            })
            .catch((error) => {
                const message = error?.response?.data || 'Sınav sonucu görüntülenemedi.';
                Swal.fire('Hata', String(message), 'error');
            });
    };

    const renderEExamActions = (seat) => {
        if (seat.examDeliveryMethod !== 'EExamFaceToFace') return null;

        if (seat.canViewEExamResult) {
            return (
                <button
                    type="button"
                    className="btn btn-sm btn-light-success mt-3"
                    onClick={() => showEExamResult(seat)}
                >
                    Sınav Sonucum
                </button>
            );
        }

        if (seat.canEnterEExam) {
            return (
                <button
                    type="button"
                    className="btn btn-sm btn-primary mt-3"
                    onClick={() => navigate(`/e-exam/${seat.examSeatAssignmentId}`)}
                >
                    Sınava Gir
                </button>
            );
        }

        return null;
    };

    const calendarEvents = useMemo(() => {
        return mobileData.map((entry) => ({
            uid: `student-exam-${entry.seat.examSeatAssignmentId || entry.seat.examScheduleId || `${entry.date}-${entry.time.startTime}-${entry.seat.examName}`}`,
            title: entry.seat.examName,
            start: createCalendarDateTime(entry.date, entry.time.startTime),
            end: createCalendarDateTime(entry.date, entry.time.endTime),
            location: [entry.seat.buildingName, entry.seat.classroomCode].filter(Boolean).join(' - '),
            description: [
                entry.periodName,
                entry.seat.seatNumber === -1 ? 'Sıra No: Sınavdan 30 dakika önce görünür' : `Sıra No: ${entry.seat.seatNumber}`,
            ]
                .filter(Boolean)
                .join('\n'),
        }));
    }, [mobileData]);

    // Render a desktop cell
    const renderCell = (date, time) => {
        const slot = timeSlots.find(
            ts =>
                ts.exactDate.startsWith(date) &&
                ts.startTime === time.startTime &&
                ts.endTime === time.endTime
        );
        if (!slot) return <div className="cell-content empty" />;
        const seat = seats.find(s => s.slotId === slot.id) || {};
        if (!seat.slotId) return <div className="cell-content empty" />;
        return (
            <div className="cell-content assigned">
                <span className="small">{seat.examName}</span>
                <span className="very-small text-muted">
                    {seat.buildingName} – {seat.classroomCode}
                </span>
                <strong className="small">
                    {seat.seatNumber === -1 ? 'Sıra No: 🔒' : `Sıra No: ${seat.seatNumber}`}</strong>

            </div>
        );
    };

    if (isLoading || examRulesApproval.isLoading || examRulesApproval.isPending) {
        return (
            <div className="d-flex justify-content-center align-items-center" style={{ height: '200px' }}>
                <div className="spinner-border" role="status">
                    <span className="visually-hidden">Yükleniyor...</span>
                </div>
            </div>
        );
    }

    if (!publishedPeriods.length) {
        return (
            <div className="alert alert-info text-center" role="alert">
                Görüntülenecek sınav takvimi bulunmamaktadır.
            </div>
        );
    }

    if (selectedPeriodIndex == null) {
        return null;
    }

    if (!rulesAccepted) {
        return (
            <div className="container mt-4">
                <h2>Sınav Takvimi</h2>
                <div className="alert alert-warning" role="alert">
                    Sınav bilgilerinizi görüntüleyebilmek için sınav kurallarını okuyup onaylamanız gerekmektedir.
                </div>
                <ExamRulesApprovalModal
                    requirement={examRulesApproval.requirement}
                    error={examRulesApproval.error}
                    isAccepting={examRulesApproval.isAccepting}
                    onAccept={examRulesApproval.accept}
                    onRetry={examRulesApproval.reload}
                    title='Sınav Kuralları Bilgilendirmesi'
                />
            </div>
        );
    }

    return (
        <div className="container mt-4">

            <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 mb-4">
                <h2 className="mb-0">Sınav Takvimi</h2>
                <CalendarExportButton
                    events={calendarEvents}
                    fileName={selectedPeriod?.name ? `${selectedPeriod.name}-sinav-takvimi` : 'sinav-takvimi'}
                />
            </div>
            {/*<h3>{publishedPeriods[selectedPeriodIndex].name}</h3>*/}

            {/* Desktop-only period tabs */}
            <div className="alert alert-danger">Sıra numarası ilgili sınavdan 30 dakika önce görünür hale gelecektir.</div>
            {publishedPeriods.length > 1 && (
                <ul className="nav nav-tabs mb-3">
                    {publishedPeriods.map((p, idx) => (
                        <li className="nav-item" key={p.id}>
                            <button
                                className={`nav-link ${selectedPeriodIndex === idx ? 'active' : ''}`}
                                onClick={() => {
                                    setSelectedPeriodIndex(idx);
                                    setCurrentWeek(0);
                                    setMobileData([]);
                                }}
                            >
                                {p.name}
                            </button>
                        </li>
                    ))}
                </ul>
            )}
            {/*<div className="d-none d-md-block">*/}
            {/*    <ul className="nav nav-tabs mb-3">*/}
            {/*        {publishedPeriods.map((p, idx) => (*/}
            {/*            <li className="nav-item" key={p.id}>*/}
            {/*                <button*/}
            {/*                    className={`nav-link ${selectedPeriodIndex === idx ? 'active' : ''}`}*/}
            {/*                    onClick={() => {*/}
            {/*                        setSelectedPeriodIndex(idx);*/}
            {/*                        setCurrentWeek(0);*/}
            {/*                    }}*/}
            {/*                >*/}
            {/*                    {p.name}*/}
            {/*                </button>*/}
            {/*            </li>*/}
            {/*        ))}*/}
            {/*    </ul>*/}
            {/*</div>*/}

            {/* Desktop: Grid */}
            {/*<div className="d-none d-md-block">*/}
            {/*    {weeks.length > 1 && (*/}
            {/*        <div className="d-flex justify-content-between mb-2">*/}
            {/*            <button*/}
            {/*                className="btn btn-sm btn-outline-primary"*/}
            {/*                onClick={() => setCurrentWeek(w => Math.max(0, w - 1))}*/}
            {/*                disabled={currentWeek === 0}*/}
            {/*            >*/}
            {/*                &larr; Ön. Hafta*/}
            {/*            </button>*/}
            {/*            <strong>*/}
            {/*                {new Date(weeks[currentWeek][0]).toLocaleDateString('tr-TR', {*/}
            {/*                    year: 'numeric', month: 'long', day: 'numeric'*/}
            {/*                })}{' '}*/}
            {/*                –{' '}*/}
            {/*                {new Date(weeks[currentWeek][6]).toLocaleDateString('tr-TR', {*/}
            {/*                    year: 'numeric', month: 'long', day: 'numeric'*/}
            {/*                })}*/}
            {/*            </strong>*/}
            {/*            <button*/}
            {/*                className="btn btn-sm btn-outline-primary"*/}
            {/*                onClick={() => setCurrentWeek(w => Math.min(weeks.length - 1, w + 1))}*/}
            {/*                disabled={currentWeek === weeks.length - 1}*/}
            {/*            >*/}
            {/*                Son. Hafta &rarr;*/}
            {/*            </button>*/}
            {/*        </div>*/}
            {/*    )}*/}
            {/*    <div style={{*/}
            {/*        overflowX: 'auto',*/}
            {/*        display: 'grid',*/}
            {/*        gridTemplateColumns: `120px repeat(${visibleDays.length},1fr)`,*/}
            {/*        gridAutoRows: '80px',*/}
            {/*        gap: '1px',*/}
            {/*        background: '#ccc'*/}
            {/*    }}>*/}
            {/*        <div style={{ background: '#f1f1f1' }} />*/}
            {/*        {visibleDays.map(d => (*/}
            {/*            <div key={d} style={{ background: '#f1f1f1', textAlign: 'center', padding: '4px' }}>*/}
            {/*                {new Date(d).toLocaleDateString('tr-TR', {*/}
            {/*                    year: 'numeric', month: 'long', day: 'numeric'*/}
            {/*                })}*/}
            {/*            </div>*/}
            {/*        ))}*/}
            {/*        {distinctTimes.map((time, i) => (*/}
            {/*            <React.Fragment key={i}>*/}
            {/*                <div style={{*/}
            {/*                    background: '#f1f1f1',*/}
            {/*                    padding: '4px',*/}
            {/*                    border: '1px solid #bbb',*/}
            {/*                    fontSize: '0.9em',*/}
            {/*                    textAlign: 'center'*/}
            {/*                }}>*/}
            {/*                    {time.startTime.substring(0, 5)} – {time.endTime.substring(0, 5)}*/}
            {/*                </div>*/}
            {/*                {visibleDays.map(d => (*/}
            {/*                    <div key={d}>{renderCell(d, time)}</div>*/}
            {/*                ))}*/}
            {/*            </React.Fragment>*/}
            {/*        ))}*/}
            {/*    </div>*/}
            {/*</div>*/}

            {/* Mobile: aggregate all periods into date cards */}
            <div className="d-block">
                {mobileDates.map(date => {
                    const entries = mobileData.filter(e => e.date === date);
                    return (
                        <div key={date} className="card mb-5">
                            <div style={{minHeight: "40px"}} className="card-header bg-warning text-white text-center fw-bold fs-5">
                                {new Date(date).toLocaleDateString('tr-TR', {
                                    year: 'numeric',
                                    month: 'long',
                                    day: 'numeric',
                                    weekday: 'long'
                                })}
                            </div>
                            <ul className="list-group list-group-flush">
                                {entries.map((e, i) => (
                                    <li key={i} className="list-group-item">
                                        <strong>
                                            {e.time.startTime.substring(0, 5)} – {e.time.endTime.substring(0, 5)}
                                        </strong><br />
                                        {e.seat.examName}<br />
                                        <small className="text-muted">
                                            {e.seat.buildingName} – {e.seat.classroomCode}
                                        </small><br />
                                        <span className="badge bg-primary">
                                            {e.seat.seatNumber === -1 ? 'Sıra No: 🔒' : `Sıra No: ${e.seat.seatNumber}`}
                                        </span>
                                        <div>{renderEExamActions(e.seat)}</div>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    );
                })}
            </div>
        </div>
    );
}
